// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Resolves the logical-node ids a plan's optimizer output refers to against the
 * monetization membership API (ADR-0012).
 *
 * The whole join: the ids in `raw_inputs.solutions[].equipment[].id` are the
 * same ids the membership API publishes for the site.
 *
 * Nothing here hardcodes a UUID — ids differ per site and per membership
 * revision, and a hardcoded one would quietly read another site's grid.
 */

import type { LogicalNode } from '../types/monetization';
import type { PlanAggregate, PlanSolution, RawInputs } from '../types';

/** Profile key of the node that aggregates a site's grid intake. */
const GRID_PROFILE_KEY = 'logical-grid';

/**
 * Profile key of the node that aggregates a site's IT load.
 *
 * The RPP panels, not the racks: an RPP meters every rack fed from it, so the
 * panel-level roll-up is the metered figure where the rack roll-up is a sum of
 * per-rack readings. Both nodes are published for GA-10.
 */
const IT_LOAD_PROFILE_KEY = 'logical-rpp';

/** Equipment type the optimizer uses for the site's grid intake. */
const SWITCHGEAR_TYPE = 'wattsch:SwitchgearType';

/**
 * The value stream the plan optimizes for, e.g. `hvac-energy-minimization`.
 * Falls back to the HVAC stream, which is the only one the optimizer emits
 * today, so a payload that omits `objective` still resolves.
 */
const DEFAULT_VALUE_STREAM = 'hvac-energy-minimization';

export function planValueStream(rawInputs: RawInputs | undefined): string {
  const term = rawInputs?.objective?.terms?.find(t => typeof t?.value_stream_id === 'string');
  return term?.value_stream_id ?? DEFAULT_VALUE_STREAM;
}

/**
 * The plan's combined-HVAC aggregate — the roll-up whose `key` matches the
 * objective's value stream. Read off the baseline solve, falling back to any
 * solve: both solves carry the same membership, and only the ids are wanted.
 *
 * This aggregate **is** the "combined HVAC logical object". There is no
 * equivalent node upstream: monetization ADR-0001 pins a node to one
 * dispatchable-unit shape, so a group spanning chillers, pumps, CRAH and IT
 * racks can only exist as a set of member ids plus a summing rule.
 */
export function hvacAggregate(rawInputs: RawInputs | undefined): PlanAggregate | undefined {
  const key = planValueStream(rawInputs);
  for (const solution of rawInputs?.solutions ?? []) {
    const match = solution.aggregated_summary?.find(a => a.key === key);
    if (match) return match;
  }
  return undefined;
}

/** The aggregate's member logical-node ids, deduplicated in payload order. */
export function hvacMemberIds(rawInputs: RawInputs | undefined): string[] {
  const ids = hvacAggregate(rawInputs)?.member_ids ?? [];
  return Array.from(new Set(ids.filter(id => typeof id === 'string' && id !== '')));
}

export interface ResolvedNodes {
  /** Member ids the membership API knows, in the aggregate's own order. */
  resolved: LogicalNode[];
  /**
   * Member ids the membership API does not publish for this site. Surfaced, not
   * dropped: it means the optimizer and the membership snapshot disagree, which
   * is an integration fault rather than an empty chart.
   */
  unresolved: string[];
}

/**
 * The organization the site's nodes belong to.
 *
 * Read off membership rather than the profile, so an org-scoped read is
 * guaranteed to use the same org as the node list it was resolved against. Every
 * node in a site's list carries the same `orgId`, so the first one answers.
 */
export function resolveOrgId(nodes: LogicalNode[]): string | undefined {
  return nodes.find(node => node.orgId)?.orgId;
}

/** Intersect a list of ids with the site's published nodes. */
export function resolveNodes(ids: string[], nodes: LogicalNode[]): ResolvedNodes {
  const byId = new Map(nodes.map(node => [node.logicalNodeId, node]));
  const resolved: LogicalNode[] = [];
  const unresolved: string[] = [];
  for (const id of ids) {
    const node = byId.get(id);
    if (node) resolved.push(node);
    else unresolved.push(id);
  }
  return { resolved, unresolved };
}

/**
 * The plan's HVAC members, resolved against the site's nodes.
 *
 * Currently uncalled: the Live view stopped reading per-member telemetry
 * because there is no grouped read and the 20-request fan-out bought nothing.
 * Kept because this is the ADR-0012 id-intersection contract, and the
 * grouped-asset redesign starts from exactly this join.
 */
export function resolveHvacNodes(
  rawInputs: RawInputs | undefined,
  nodes: LogicalNode[]
): ResolvedNodes {
  return resolveNodes(hvacMemberIds(rawInputs), nodes);
}

/** The first equipment of a type across any solve. */
function findEquipmentId(solutions: PlanSolution[] | undefined, type: string): string | undefined {
  for (const solution of solutions ?? []) {
    const match = solution.equipment?.find(e => e.type === type);
    if (match?.id) return match.id;
  }
  return undefined;
}

/**
 * The site's grid node, found by aggregation profile — never by a literal id.
 *
 * The plan's single switchgear equipment id should be the same node. When the
 * two disagree the profile match wins (monetization is the system of record for
 * membership) and the mismatch is logged, because it means the plan was solved
 * against a different grid node than the site currently publishes.
 */
export function resolveNodeByProfile(
  nodes: LogicalNode[],
  profileKey: string
): LogicalNode | undefined {
  return nodes.find(node => node.aggregationProfileRef?.key === profileKey);
}

/**
 * The site's IT-load node — the RPP roll-up.
 *
 * Profile match only, unlike {@link resolveGridNode}: the plan carries IT load
 * as a bare `raw_inputs.it_load_forecast` signal map with no equipment entry,
 * so there is no plan-side id to cross-check the membership answer against.
 */
export function resolveItLoadNode(nodes: LogicalNode[]): LogicalNode | undefined {
  return resolveNodeByProfile(nodes, IT_LOAD_PROFILE_KEY);
}

export function resolveGridNode(
  nodes: LogicalNode[],
  rawInputs?: RawInputs
): LogicalNode | undefined {
  const byProfile = resolveNodeByProfile(nodes, GRID_PROFILE_KEY);
  const fromPlan = findEquipmentId(rawInputs?.solutions, SWITCHGEAR_TYPE);

  if (byProfile && fromPlan && byProfile.logicalNodeId !== fromPlan) {
    // eslint-disable-next-line no-console
    console.warn(
      `Grid node mismatch: membership publishes ${byProfile.logicalNodeId} for profile ` +
        `${GRID_PROFILE_KEY}, the plan was solved against ${fromPlan}. Using the published node.`
    );
  }
  if (byProfile) return byProfile;

  // No grid profile at this site: fall back to the plan's switchgear id if the
  // membership API happens to publish it under another profile.
  return fromPlan ? nodes.find(node => node.logicalNodeId === fromPlan) : undefined;
}
